import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { cn } from '@rekrytering/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const SCORE_MAX = 5

const ScoreBar = ({ score }: { score: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: SCORE_MAX }).map((_, i) => (
      <div
        key={i}
        className={cn(
          'h-1.5 flex-1 rounded-full',
          i < score ? 'bg-primary' : 'bg-border',
        )}
      />
    ))}
  </div>
)

type Props = {
  readonly jobId: Id<'jobs'>
}

export const AggregateReportTab = ({ jobId }: Props) => {
  const { t } = useTranslation()
  const { data: report } = useQuery(
    convexQuery(api.features.interviews.aggregate_report.getMyAggregateReport, {
      jobId,
    }),
  )

  if (!report || report.rankings.length === 0) {
    return (
      <div className="flex items-center justify-center py-24 text-primary/40">
        <p className="text-sm">
          {t('interviews.aggregateReport.noCompletedInterviews')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {report.rankings.map((ranking) => (
        <div
          key={ranking.interviewId}
          className={cn(
            'flex flex-col gap-3 rounded-lg border px-4 py-3',
            ranking.isTopN ? 'border-primary/40 bg-primary/5' : 'border-border',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold text-primary/60">
                {ranking.rank}
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-primary">
                  {ranking.candidateName}
                </span>
                {ranking.scores.length > 0 && (
                  <span className="text-xs text-primary/50">
                    {t('interviews.aggregateReport.averageScore')}:{' '}
                    {ranking.averageScore.toFixed(1)}/{SCORE_MAX}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {ranking.isTopN && (
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                  {t('interviews.aggregateReport.topN', {
                    n: String(report.topNCount),
                  })}
                </span>
              )}
              <Link
                to="/dashboard/interviews/$interviewId"
                params={{ interviewId: ranking.interviewId }}
                className="text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                {t('interviews.candidates.viewReport')}
              </Link>
            </div>
          </div>

          {ranking.scores.length > 0 && (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {ranking.scores.map((score) => (
                <div key={score.competency} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="truncate text-xs text-primary/60">
                      {score.competency}
                    </span>
                    <span className="shrink-0 text-xs font-medium text-primary/70">
                      {score.score}/{SCORE_MAX}
                    </span>
                  </div>
                  <ScoreBar score={score.score} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
